import { useCallback, useEffect, useRef, useState, type MutableRefObject } from "react";

export type RoomSocketRole = "voter" | "curator";

function getOrCreateVoterId(roomId: string): string {
	const k = `wi-voter-${roomId}`;
	let v = localStorage.getItem(k);
	if (!v) {
		v = crypto.randomUUID();
		localStorage.setItem(k, v);
	}
	return v;
}

/**
 * Optional ref: assign `ref.current = (msg) => { ... }` each render so every WS frame is handled
 * (vote_accepted is not lost when the next message is tally_update).
 */
export function useRoomSocket(
	roomId: string | undefined,
	role: RoomSocketRole,
	enabled: boolean,
	onWsFrameRef?: MutableRefObject<((msg: unknown) => void) | undefined>,
) {
	const [connected, setConnected] = useState(false);
	const [upgradeRejected, setUpgradeRejected] = useState(false);
	const [lastEvent, setLastEvent] = useState<unknown>(null);
	const wsRef = useRef<WebSocket | null>(null);

	const send = useCallback((obj: object) => {
		wsRef.current?.send(JSON.stringify(obj));
	}, []);

	useEffect(() => {
		if (!roomId || !enabled) return;
		setUpgradeRejected(false);
		let opened = false;
		const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
		const url = `${proto}//${window.location.host}/api/rooms/${encodeURIComponent(roomId)}/ws?role=${role}`;
		const ws = new WebSocket(url);
		wsRef.current = ws;
		ws.onopen = () => {
			opened = true;
			setConnected(true);
			if (role === "voter") {
				const voterId = getOrCreateVoterId(roomId);
				ws.send(JSON.stringify({ type: "hello", role: "voter", voterId }));
			} else {
				ws.send(JSON.stringify({ type: "hello", role: "curator" }));
			}
		};
		ws.onmessage = (ev) => {
			const raw = String(ev.data);
			let parsed: unknown;
			try {
				parsed = JSON.parse(raw);
			} catch {
				parsed = { type: "parse_error", raw: raw.slice(0, 500) };
			}
			setLastEvent(parsed);
			onWsFrameRef?.current?.(parsed);
		};
		ws.onclose = () => {
			if (!opened) setUpgradeRejected(true);
			setConnected(false);
			wsRef.current = null;
		};
		return () => {
			ws.close();
			wsRef.current = null;
			setConnected(false);
		};
	}, [roomId, role, enabled, onWsFrameRef]);

	return { connected, upgradeRejected, lastEvent, send };
}
